import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Timeline from "../components/Timeline";
import "./About.css";
import portrait from "../assets/images/portrait.jpg";
import background from "../assets/images/aboutbanner.jpg";
import { motion } from "framer-motion";

const About = () => {
  return (
    <>
      <Navbar />

      {/* =========================
          ABOUT HERO
      ========================== */}
      <section
        className="about-clinic-section"
        style={{ backgroundImage: `url(${background})` }}
      >
        <div className="about-hero-overlay"></div>

        <motion.div
          className="about-clinic-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <span className="about-eyebrow">WELCOME TO</span>

          <h1 className="clinic-name">Sudhamshu Ayurveda</h1>

          <div className="hero-divider"></div>

          <h2 className="clinic-subtitle">
            Rooted in Tradition, Guided by Ayurveda Shastra
          </h2>

          <p>
            At Sudhamshu Ayurveda, we believe that good health is not merely
            the absence of illness—it is the foundation for living a
            purposeful, prosperous, joyful, and spiritually fulfilling life.
          </p>

          <p>
            Our treatments are rooted in restoring balance, nurturing
            vitality, and aligning the body with deeper life goals.
          </p>

          <p>
            Founded by <strong>Dr. Vaishnavi K M</strong>, an award-winning
            Ayurvedic physician and researcher, Sudhamshu Ayurveda combines
            classical Ayurvedic wisdom with scientific insights to provide
            holistic, evidence-based care tailored to each individual.
          </p>

          <div className="hero-highlight">
            <span>Personalized Care</span>
            <span>•</span>
            <span>Classical Ayurveda</span>
            <span>•</span>
            <span>Holistic Healing</span>
          </div>
        </motion.div>
      </section>

      {/* =========================
          DOCTOR SECTION
      ========================== */}
      <section className="about-section" aria-label="About the Doctor">
        <motion.div
          className="knowyourdoc"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* Doctor Image */}
          <div className="about-image">
            <div className="portrait-frame">
              <img
                src={portrait}
                alt="Dr. Vaishnavi K M"
                className="portrait-img"
              />

              <div className="doctor-image-caption">
                <span>Founder &amp; Chief Ayurvedic Consultant</span>
              </div>
            </div>
          </div>

          {/* Doctor Content */}
          <div className="about-content">
            <span className="section-eyebrow">MEET THE DOCTOR</span>

            <h2>Dr. Vaishnavi K M</h2>

            <p className="doctor-qualification">
              BAMS, MD (Ayurveda)
            </p>

            <p className="doctor-role">
              Founder &amp; Chief Ayurvedic Consultant
            </p>

            <div className="content-divider"></div>

            <p>
              Dr. Vaishnavi K M is an Ayurvedic physician and the founder of
              Sudhamshu Ayurveda, Bengaluru. Specializing in Rasashastra and
              Bhaishajya Kalpana (Ayurvedic Pharmaceutics), she brings over six
              years of clinical and research experience, seamlessly
              integrating classical Ayurvedic wisdom with scientific
              approaches.
            </p>

            <p>
              A dedicated consultant, educator, and researcher, Dr. Vaishnavi
              has treated more than 500 patients, emphasizing a personalized
              and holistic approach to healing.
            </p>

            <h3>Biography</h3>

            <p>
              Dr. Vaishnavi has authored eight research articles in national
              and international journals and completed a thesis on
              Tripurabhairava Rasa focused on its antifungal effects in
              mucormycosis.
            </p>

            <p>
              She has actively participated in over 80 seminars and
              conferences across India. Her professional experience spans
              notable institutions including NIMHANS, KC General Hospital, and
              Sri Dhatri Ayurvedalaya.
            </p>

            {/* Trust Statistics */}
            <div className="doctor-stats">
              <div className="stat-card">
                <strong>500+</strong>
                <span>Patients Treated</span>
              </div>

              <div className="stat-card">
                <strong>8</strong>
                <span>Research Articles</span>
              </div>

              <div className="stat-card">
                <strong>80+</strong>
                <span>Seminars &amp; Conferences</span>
              </div>

              <div className="stat-card">
                <strong>6+</strong>
                <span>Years Experience</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="doctor-social">
              <a
                href="https://www.facebook.com/share/1EvTWrhJeY/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="icon-circle"
              >
                <i className="fab fa-facebook-f"></i>
              </a>

              <a
                href="https://www.instagram.com/sudhamshu.ayurveda?igsh=cXJzODJwYXY5dDk2"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="icon-circle"
              >
                <i className="fab fa-instagram"></i>
              </a>

              <a
                href="https://youtube.com/@sudhamshuayurveda?si=m8V9lXQ0kwjzRA3L"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="icon-circle"
              >
                <i className="fab fa-youtube"></i>
              </a>

              <a
                href="https://www.linkedin.com/in/dr-vaishnavi-km-64808b224?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="icon-circle"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
            </div>
          </div>
        </motion.div>
      </section>

      {/* =========================
          PROFESSIONAL JOURNEY
      ========================== */}
      <section className="journey-intro">
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <span className="section-eyebrow">PROFESSIONAL JOURNEY</span>

          <h2>Experience Built on Learning &amp; Recognition</h2>

          <p>
            Explore the academic achievements, research contributions,
            specialization, and professional recognition that have shaped
            Dr. Vaishnavi's journey in Ayurveda.
          </p>
        </motion.div>
      </section>

      <Timeline />

      {/* =========================
          FINAL CTA
      ========================== */}
      <section className="about-cta">
        <div className="about-cta-content">
          <span className="section-eyebrow">YOUR WELLNESS JOURNEY</span>

          <h2>Begin Your Journey Towards Better Health</h2>

          <p>
            Discover a personalized approach to wellness rooted in the
            principles of Ayurveda.
          </p>

          <a href="/contact" className="about-cta-button">
            Book an Appointment
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default About;